import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import Thumb from '../components/Thumb';
import { HardDrive, CheckCircle2, Clock, AlertTriangle, Radio, ChevronRight, MoreVertical, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Media Management — kanban board for the media offload lifecycle.
 *
 * All items in the "Media" category surface here, grouped by `media_status`.
 * Click a card's next-step button to advance it through the workflow.
 *
 *   Ready ─(auto on Pack)→ In Use ─(auto on Return)→ Needs Offload
 *          ↑                                                │
 *          └──────── Mark Ready ←──── Verified ←─(manual)──┘
 *
 * The gate lives on the backend — adding a non-Ready media card to a project
 * returns 409. This page is where the user resolves that.
 */

// Column definitions — column `key` matches the DB status; `label` is the
// human-readable column header.
const COLUMNS = [
  {
    key: 'Needs Offload',
    label: 'Needs Offload',
    icon: AlertTriangle,
    accent: 'rose',
    subtitle: 'Returned from set — transfer footage next.',
    nextStatus: 'Verifying',
    nextLabel: 'Mark Verifying',
  },
  {
    key: 'Verifying',
    label: 'Verifying',
    icon: CheckCircle2,
    accent: 'sky',
    subtitle: 'Transferred and confirmed. Wipe, then mark Ready.',
    nextStatus: 'Ready',
    nextLabel: 'Mark Ready',
  },
  {
    key: 'Ready',
    label: 'Ready',
    icon: Radio,
    accent: 'emerald',
    subtitle: 'Clean, empty, safe to check out.',
    nextStatus: null,
    nextLabel: null,
  },
  {
    key: 'In Use',
    label: 'In Use',
    icon: Clock,
    accent: 'amber',
    subtitle: 'Currently checked out to a project.',
    nextStatus: null, // moves automatically when returned
    nextLabel: null,
  },
];

// Utility — tailwind classes keyed by column accent so the JSX stays readable.
const ACCENT = {
  rose: {
    border: 'border-rose-500/30',
    bg: 'bg-rose-500/5',
    text: 'text-rose-300',
    icon: 'text-rose-400',
    countBg: 'bg-rose-500/15 text-rose-200',
  },
  sky: {
    border: 'border-sky-500/30',
    bg: 'bg-sky-500/5',
    text: 'text-sky-300',
    icon: 'text-sky-400',
    countBg: 'bg-sky-500/15 text-sky-200',
  },
  emerald: {
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/5',
    text: 'text-emerald-300',
    icon: 'text-emerald-400',
    countBg: 'bg-emerald-500/15 text-emerald-200',
  },
  amber: {
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/5',
    text: 'text-amber-300',
    icon: 'text-amber-400',
    countBg: 'bg-amber-500/15 text-amber-200',
  },
};

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = Date.now() - then;
  const day = 86400_000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  const days = Math.floor(diff / day);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// All statuses in order — used by the manual "Change status" menu.
const ALL_STATUSES = ['Ready', 'In Use', 'Needs Offload', 'Verifying'];

// Compact status pill (dot + label) used inside the change-status menu so
// the user sees the accent color they'll be moving the card to.
function StatusPill({ status }) {
  const accent =
    status === 'Ready' ? 'emerald' :
    status === 'In Use' ? 'amber' :
    status === 'Needs Offload' ? 'rose' : 'sky';
  const a = ACCENT[accent];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] ${a.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${a.countBg}`.replace('countBg', '')} style={{
        background:
          accent === 'emerald' ? '#10b981' :
          accent === 'amber' ? '#f59e0b' :
          accent === 'rose' ? '#f43f5e' : '#0ea5e9',
      }} />
      {status}
    </span>
  );
}

function MediaCard({ item, column, projectUsing, onTransition, onOpenItem }) {
  const acc = ACCENT[column.accent];
  const last = relativeTime(item.mediaLastOffloadAt);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const advance = async () => {
    if (!column.nextStatus) return;
    setBusy(true);
    try {
      await onTransition(item.code, column.nextStatus);
    } finally {
      setBusy(false);
    }
  };

  const manualSet = async (target) => {
    setMenuOpen(false);
    if (target === item.mediaStatus) return;
    setBusy(true);
    try {
      await onTransition(item.code, target);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`relative rounded-md border ${acc.border} ${acc.bg} p-3 space-y-2`}
      data-testid={`media-card-${item.code}`}
    >
      {/* Manual status override — small kebab in the top-right that lets the
          user jump to any status, bypassing the primary forward-only flow. */}
      <div className="absolute top-2 right-2">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          className="h-6 w-6 grid place-items-center rounded text-neutral-500 hover:text-white hover:bg-white/5 disabled:opacity-50"
          title="Change status manually"
          data-testid={`media-menu-btn-${item.code}`}
        >
          <MoreVertical size={13} />
        </button>
        {menuOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setMenuOpen(false)}
              aria-hidden
            />
            <div
              className="absolute right-0 top-7 z-20 w-44 rounded-md border border-neutral-800 bg-neutral-950 shadow-lg overflow-hidden"
              data-testid={`media-menu-${item.code}`}
            >
              <div className="text-[9px] tracking-wider uppercase text-neutral-500 px-3 pt-2 pb-1">
                Set status to
              </div>
              {ALL_STATUSES.map((s) => {
                const active = s === item.mediaStatus;
                return (
                  <button
                    key={s}
                    onClick={() => manualSet(s)}
                    disabled={active}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-2 ${
                      active
                        ? 'bg-neutral-900 cursor-default'
                        : 'hover:bg-neutral-900'
                    }`}
                    data-testid={`media-menu-item-${item.code}-${s.replace(/\s+/g, '-')}`}
                  >
                    <StatusPill status={s} />
                    {active && <span className="text-[9px] text-neutral-600">current</span>}
                  </button>
                );
              })}
              <div className="border-t border-neutral-800 my-1" />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  // Popup mirrors the existing inventory print flow — same
                  // window.open shape so the popup-aware Close button works.
                  window.open(
                    `/print-label/media/${item.code}`,
                    'wfc-print-label',
                    'width=520,height=640',
                  );
                }}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-amber-300 hover:bg-neutral-900"
                data-testid={`media-menu-print-${item.code}`}
              >
                <Printer size={11} /> Print status QR
              </button>
            </div>
          </>
        )}
      </div>

      <div className="flex items-start gap-2 pr-6">
        <Thumb src={item.image} name={item.name} />
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onOpenItem(item)}
            className="text-left w-full"
            title="View item history"
          >
            <div className="text-sm text-white truncate hover:text-amber-300">{item.name}</div>
            <div className="text-[10px] text-neutral-500 flex items-center gap-2 flex-wrap">
              <span className="font-mono">{item.code}</span>
              {item.mediaCapacity && <span>· {item.mediaCapacity}</span>}
              {item.mediaFormat && <span>· {item.mediaFormat}</span>}
            </div>
          </button>
        </div>
      </div>

      {(projectUsing || last) && (
        <div className="text-[10px] text-neutral-500 pt-1 border-t border-neutral-800/60">
          {projectUsing && (
            <div className="flex items-center gap-1">
              <span className="text-neutral-600">on:</span>
              <span className="text-neutral-300 truncate">{projectUsing}</span>
            </div>
          )}
          {last && column.key === 'Ready' && (
            <div className="flex items-center gap-1">
              <span className="text-neutral-600">last offload:</span>
              <span className="text-neutral-400">{last}</span>
            </div>
          )}
        </div>
      )}

      {column.nextStatus && (
        <button
          onClick={advance}
          disabled={busy}
          className={`w-full mt-2 text-[11px] px-2 py-1.5 rounded border ${acc.border} ${acc.text} hover:bg-white/5 disabled:opacity-50 inline-flex items-center justify-center gap-1`}
          data-testid={`media-advance-${item.code}`}
        >
          {busy ? 'Saving…' : column.nextLabel}
          <ChevronRight size={11} />
        </button>
      )}
    </div>
  );
}

export default function MediaManagement() {
  const { items, projects, setMediaStatus } = useApp();
  const navigate = useNavigate();

  const media = useMemo(() => items.filter((i) => i.category === 'Media'), [items]);

  // Build a "which project has this item packed" lookup so the "In Use" column
  // can annotate cards with the shoot they're on. Falls back to any project
  // that has the code reserved (item_codes) if it isn't packed yet.
  const projectByCode = useMemo(() => {
    const m = new Map();
    projects.forEach((p) => {
      if (p.status === 'completed') return;
      const codes = new Set([...(p.itemCodes || []), ...Object.keys(p.packed || {})]);
      codes.forEach((c) => {
        if (!m.has(c)) m.set(c, p);
      });
    });
    return m;
  }, [projects]);

  const grouped = useMemo(() => {
    const g = { Ready: [], 'In Use': [], 'Needs Offload': [], Verifying: [] };
    media.forEach((m) => {
      const s = m.mediaStatus || 'Ready';
      if (g[s]) g[s].push(m);
      else g.Ready.push(m);
    });
    return g;
  }, [media]);

  const totalUnready = grouped['Needs Offload'].length + grouped.Verifying.length;

  const handleTransition = async (code, next) => {
    try {
      await setMediaStatus(code, next);
      toast.success(`${code} → ${next}`);
    } catch (e) {
      toast.error(e.message || 'Could not update status');
    }
  };

  const openItem = (item) => {
    navigate(`/inventory/${item.code}`);
  };

  return (
    <div className="p-5" data-testid="media-management-page">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-1">
            Workflow
          </div>
          <h1 className="text-2xl text-white flex items-center gap-2">
            <HardDrive size={20} className="text-amber-400" /> Media Management
          </h1>
          <p className="text-xs text-neutral-500 mt-1 max-w-2xl">
            Track memory cards and SSDs through the offload workflow.
            Media has to be in <span className="text-emerald-400">Ready</span>{' '}
            before it can be added to a project — the checkout screen will
            block anything else.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <div className="text-neutral-400">
            <span className="text-white font-semibold">{media.length}</span> media items
          </div>
          {totalUnready > 0 && (
            <div className="px-2 py-1 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300">
              <span className="font-semibold">{totalUnready}</span> awaiting action
            </div>
          )}
        </div>
      </div>

      {media.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center">
          <HardDrive size={32} className="mx-auto text-neutral-700 mb-3" />
          <p className="text-sm text-neutral-400">
            No media items yet. Add items with category &quot;Media&quot; from Main
            Inventory to populate this board.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMNS.map((col) => {
            const acc = ACCENT[col.accent];
            const cards = grouped[col.key] || [];
            const Icon = col.icon;
            return (
              <div
                key={col.key}
                className="surface rounded-lg flex flex-col"
                data-testid={`media-column-${col.key.replace(/\s+/g, '-')}`}
              >
                <div className="px-3 py-2.5 rounded-t-lg border-b border-neutral-900 bg-neutral-950 flex items-center gap-2">
                  <Icon size={13} className={acc.icon} />
                  <span className={`text-xs font-semibold ${acc.text}`}>{col.label}</span>
                  <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded ${acc.countBg}`}>
                    {cards.length}
                  </span>
                </div>
                <div className="text-[10px] text-neutral-500 px-3 py-1.5 border-b border-neutral-900/60 leading-relaxed">
                  {col.subtitle}
                </div>
                <div className="p-2 space-y-2 flex-1 min-h-[120px]">
                  {cards.length === 0 ? (
                    <div className="text-[10px] text-neutral-700 italic text-center py-4">
                      — empty —
                    </div>
                  ) : (
                    cards.map((c) => (
                      <MediaCard
                        key={c.code}
                        item={c}
                        column={col}
                        projectUsing={projectByCode.get(c.code)?.name}
                        onTransition={handleTransition}
                        onOpenItem={openItem}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
